import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  ArrowLeft, BarChart3, DollarSign, Target, AlertTriangle, Users, Package,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#94a3b8'];

const Stat = ({ icon: Icon, label, value, hint, color = 'text-blue-600' }) => (
  <Card>
    <CardContent className="p-4 flex items-start gap-3">
      <div className={`mt-1 ${color}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
    </CardContent>
  </Card>
);

const PMAnalytics = ({ user }) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchAll = async (pid = 'all') => {
    try {
      setLoading(true);
      const url = pid && pid !== 'all'
        ? `${BACKEND_URL}/api/pm/analytics?project_id=${pid}`
        : `${BACKEND_URL}/api/pm/analytics`;
      const [a, p] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${BACKEND_URL}/api/projects`, { headers }),
      ]);
      setData(a.data);
      setProjects(p.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(filterProject); /* eslint-disable-next-line */ }, [filterProject]);

  const statusData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.summary.by_status).map(([k, v]) => ({ name: k, value: v }));
  }, [data]);

  const phaseData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.summary.by_phase).map(([k, v]) => ({ name: k, value: v }));
  }, [data]);

  const severityData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.risks.by_severity).map(([k, v]) => ({ name: k, value: v }));
  }, [data]);

  const perProjectChart = useMemo(() => {
    if (!data) return [];
    return data.per_project.slice(0, 10).map((p) => ({
      name: p.title.length > 20 ? p.title.slice(0, 18) + '…' : p.title,
      progress: p.progress,
      kpiProgress: p.kpi_avg_progress_pct,
      budgetUtil: p.budget_utilisation_pct,
    }));
  }, [data]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 text-center text-gray-500">Loading analytics…</div>
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-1">
              <ArrowLeft className="h-3 w-3 mr-1" /> Back
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="h-6 w-6 mr-2 text-blue-600" /> Project Analytics
            </h1>
            <p className="text-sm text-gray-500">Cross-project rollup of KPIs, financials, risks, deliverables and beneficiaries</p>
          </div>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-[260px]" data-testid="pm-analytics-project-filter">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects ({projects.length})</SelectItem>
              {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat icon={Package} label="Projects" value={s.total_projects} hint={`${s.avg_progress}% avg progress`} color="text-blue-600" />
          <Stat icon={DollarSign} label="Total Budget"
                value={`€${(s.total_budget || 0).toLocaleString('el-GR')}`}
                hint={`${s.budget_utilisation_pct}% utilised`} color="text-emerald-700" />
          <Stat icon={Target} label="KPIs"
                value={data.kpis.total_kpis}
                hint={`${data.kpis.on_target_count} on-target (${data.kpis.on_target_pct}%)`} color="text-purple-600" />
          <Stat icon={AlertTriangle} label="Open risks"
                value={data.risks.open_risks}
                hint={`${data.risks.total_risks} total`} color="text-amber-600" />
          <Stat icon={Package} label="Deliverables"
                value={data.deliverables.total_deliverables}
                hint={`${data.deliverables.overdue} overdue`} color="text-rose-600" />
          <Stat icon={Users} label="Beneficiaries" value={data.beneficiaries_total} color="text-cyan-700" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Projects by status</CardTitle></CardHeader>
            <CardContent style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                    {statusData.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Projects by phase</CardTitle></CardHeader>
            <CardContent style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={phaseData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                    {phaseData.map((_, i) => (<Cell key={i} fill={PIE_COLORS[(i + 2) % PIE_COLORS.length]} />))}
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Risks by severity</CardTitle></CardHeader>
            <CardContent style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={severityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" /><YAxis allowDecimals={false} />
                  <Tooltip /><Bar dataKey="value" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Per-project chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center"><TrendingUp className="h-4 w-4 mr-2 text-blue-600" />
              Top projects: progress vs KPI vs budget utilisation
            </CardTitle>
          </CardHeader>
          <CardContent style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perProjectChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend />
                <Bar dataKey="progress" name="Progress" fill="#3b82f6" />
                <Bar dataKey="kpiProgress" name="KPI avg" fill="#10b981" />
                <Bar dataKey="budgetUtil" name="Budget used" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Per-project table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Per-project breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-gray-500">
                    <th className="py-2 pr-3">Project</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Phase</th>
                    <th className="py-2 pr-3 text-right">Progress</th>
                    <th className="py-2 pr-3 text-right">KPIs</th>
                    <th className="py-2 pr-3 text-right">Budget</th>
                    <th className="py-2 pr-3 text-right">Utilised</th>
                    <th className="py-2 pr-3 text-right">Open risks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_project.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`pm-row-${p.id}`}>
                      <td className="py-2 pr-3">
                        <Link to={`/projects/${p.id}`} className="text-blue-600 hover:underline">{p.title}</Link>
                      </td>
                      <td className="py-2 pr-3"><Badge variant="outline" className="capitalize">{p.status}</Badge></td>
                      <td className="py-2 pr-3 text-gray-600 capitalize">{(p.phase || '').replace(/_/g, ' ')}</td>
                      <td className="py-2 pr-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Progress value={p.progress} className="w-16 h-1.5" />
                          <span className="text-gray-700">{Math.round(p.progress)}%</span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-700">
                        {p.kpi_count > 0 ? `${p.kpi_avg_progress_pct}% (${p.kpi_on_target}/${p.kpi_count})` : '—'}
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-700">€{(p.budget || 0).toLocaleString('el-GR')}</td>
                      <td className="py-2 pr-3 text-right">
                        <span className={p.budget_utilisation_pct > 100 ? 'text-red-600 font-medium' : 'text-gray-700'}>
                          {p.budget_utilisation_pct}%
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {p.open_risks > 0 ? <Badge className="bg-amber-100 text-amber-800">{p.open_risks}</Badge> : <span className="text-gray-500">0</span>}
                      </td>
                    </tr>
                  ))}
                  {data.per_project.length === 0 && (
                    <tr><td colSpan="8" className="py-6 text-center text-gray-500">No projects in scope</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="text-xs text-gray-500 text-right">Generated {new Date(data.generated_at).toLocaleString('en-GB')}</div>
      </div>
    </div>
  );
};

export default PMAnalytics;
